import { useTranslation } from 'react-i18next'
import { languages, changeLanguage } from '@/i18n'
import { Button } from '@/components/ui/button'
import { Globe } from 'lucide-react'
import { cn } from '@/lib/utils'

interface LanguageSwitcherProps {
  variant?: 'dropdown' | 'buttons' | 'minimal'
  className?: string
}

export function LanguageSwitcher({ variant = 'minimal', className }: LanguageSwitcherProps) {
  const { i18n } = useTranslation()
  const currentLang = i18n.language?.split('-')[0] || 'en'

  if (variant === 'buttons') {
    return (
      <div className={cn('flex gap-1', className)}>
        {languages.map((lang) => (
          <Button
            key={lang.code}
            variant={currentLang === lang.code ? 'default' : 'ghost'}
            size="sm"
            onClick={() => changeLanguage(lang.code)}
            className="px-2"
          >
            <span className="mr-1">{lang.flag}</span>
            {lang.code.toUpperCase()}
          </Button>
        ))}
      </div>
    )
  }

  if (variant === 'dropdown') {
    return (
      <div className={cn('relative group', className)}>
        <Button variant="ghost" size="sm" className="gap-2">
          <Globe className="h-4 w-4" />
          <span>{languages.find(l => l.code === currentLang)?.flag}</span>
          <span className="hidden sm:inline">
            {languages.find(l => l.code === currentLang)?.name}
          </span>
        </Button>
        <div className="absolute right-0 mt-1 w-40 rounded-md border bg-popover shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => changeLanguage(lang.code)}
              className={cn(
                'w-full px-3 py-2 text-left text-sm hover:bg-accent flex items-center gap-2',
                currentLang === lang.code && 'bg-accent'
              )}
            >
              <span>{lang.flag}</span>
              <span>{lang.name}</span>
            </button>
          ))}
        </div>
      </div>
    )
  }

  // Minimal variant - just flags
  return (
    <div className={cn('flex items-center gap-1', className)}>
      {languages.map((lang) => (
        <button
          key={lang.code}
          onClick={() => changeLanguage(lang.code)}
          className={cn(
            'text-lg px-1 py-0.5 rounded transition-all hover:bg-accent',
            currentLang === lang.code && 'bg-accent ring-1 ring-primary'
          )}
          title={lang.name}
        >
          {lang.flag}
        </button>
      ))}
    </div>
  )
}
